"use client";
import React, { useRef, useCallback, useEffect, useState } from "react";
import { ApiError } from "@/lib/apiClient";
import { Page, Section } from "@/types/models/page";
import { CanvasContent } from "./CanvasContent";
import { useAppDispatch } from "@/store/hooks";
import { addSection } from "@/store/slices/webbuilderSlice";

interface CanvasProps {
  deviceWidth: number;
  isCanvasShimmering: boolean;
  currentDevice: "desktop" | "tablet" | "mobile";
  pages: Page[];
  pagesLoading: boolean;
  pagesError: ApiError | null;
  currentPage: Page | null;
  currentPageLoading: boolean;
  currentPageError: ApiError | null;
  onRetryFetch: () => void;
  onCreatePage: () => void;
  selectedSectionId?: string | null;
  onSectionSelect?: (section: Section | null) => void;
  handleCloseSectionPanel?: () => void;
  storeId: string;
  fullscreenPreview?: boolean;
}

export const Canvas = React.memo(function Canvas({
  deviceWidth,
  isCanvasShimmering,
  currentDevice,
  pages,
  pagesLoading,
  pagesError,
  currentPage,
  currentPageLoading,
  currentPageError,
  onRetryFetch,
  onCreatePage,
  selectedSectionId,
  onSectionSelect,
  handleCloseSectionPanel,
  storeId,
  fullscreenPreview = false,
}: CanvasProps) {
  console.log("📦 Canvas render:", { currentDevice, pageId: currentPage?.id });
  const shimmerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const [dropIndicatorIndex, setDropIndicatorIndex] = useState<number | null>(
    null,
  );

  // Add debugging for keyboard events in Canvas
  useEffect(() => {
    const canvasKeyHandler = () => {
      // Canvas keyboard event handler - no logging needed
    };

    // Listen for keyboard events at the Canvas level
    document.addEventListener("keydown", canvasKeyHandler);

    return () => {
      document.removeEventListener("keydown", canvasKeyHandler);
    };
  }, []);

  // Handle drop events for sections
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";

      // Calculate drop position based on mouse Y coordinate
      if (canvasRef.current && currentPage?.sections) {
        const canvasRect = canvasRef.current.getBoundingClientRect();
        const mouseY = e.clientY - canvasRect.top + canvasRef.current.scrollTop;

        const sectionElements =
          canvasRef.current.querySelectorAll(".canvas-section");

        if (sectionElements.length === 0) {
          // If no sections, drop at index 0
          setDropIndicatorIndex(0);
          return;
        }

        let insertIndex = sectionElements.length; // Default to end

        // Find the section that the cursor is closest to
        for (let i = 0; i < sectionElements.length; i++) {
          const element = sectionElements[i] as HTMLElement;
          const rect = element.getBoundingClientRect();
          const elementTop =
            rect.top - canvasRect.top + canvasRef.current.scrollTop;
          const elementMiddle = elementTop + rect.height / 2;

          if (mouseY < elementMiddle) {
            insertIndex = i;
            break;
          }
        }

        setDropIndicatorIndex(insertIndex);
      }
    },
    [currentPage],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();

      try {
        const data = e.dataTransfer.getData("text/plain");
        const parsedData = JSON.parse(data);

        if (parsedData.type === "section" && parsedData.data) {
          const sectionData = parsedData.data as Section;
          console.log("🔵 Canvas: Adding section to canvas:", sectionData);
          console.log("🔵 Canvas: Drop indicator index:", dropIndicatorIndex);

          // Add the section to the Redux store with the calculated position
          dispatch(
            addSection({
              section: sectionData,
              insertAtIndex: dropIndicatorIndex ?? undefined,
            }),
          );
        }
      } catch (error) {
        console.error("Error parsing dropped data:", error);
      } finally {
        // Clear the drop indicator
        setDropIndicatorIndex(null);
      }
    },
    [dispatch, dropIndicatorIndex],
  );

  // Handle drag leave to clear drop indicator
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Only clear if leaving the canvas entirely
    if (e.currentTarget === e.target) {
      setDropIndicatorIndex(null);
    }
  }, []);

  // Handle click outside to close section panel
  const handleMainClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && handleCloseSectionPanel) {
        // Check if the click is within the form area (right sidebar)
        const target = e.target as Element;
        const formPanel = document.querySelector("[data-form-panel]");
        if (formPanel && formPanel.contains(target)) {
          return;
        }
        handleCloseSectionPanel();
      }
    },
    [handleCloseSectionPanel],
  );

  // Prevent mouse events from bubbling up to parent containers
  const handleMainMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) {
      e.stopPropagation();
    }
  }, []);

  return (
    <div
      className={
        fullscreenPreview
          ? "h-full w-full"
          : "flex-1 flex items-center justify-center p-4"
      }
    >
      <main
        ref={canvasRef}
        className={`relative bg-card dark:bg-auth-card rounded-3xl border border-border dark:border-auth-background shadow-2xl overflow-y-auto transition-all duration-300 ease-in-out ${isCanvasShimmering ? "animate-shimmer" : ""}`}
        style={{
          width: `${deviceWidth}px`,
          maxHeight: fullscreenPreview ? "100%" : "calc(100vh - 8rem)",
          margin: fullscreenPreview ? "0 auto" : undefined,
        }}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragLeave={handleDragLeave}
        onClick={handleMainClick}
        onMouseDown={handleMainMouseDown}
      >
        {/* Canvas Shimmer Overlay */}
        {isCanvasShimmering && (
          <div
            ref={shimmerRef}
            className="absolute inset-0 bg-gradient-shimmer -skew-x-12 animate-shimmerSlide pointer-events-none z-10"
          />
        )}

        {/* Canvas Content */}
        {pagesLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-4">
              <div className="relative w-16 h-16 mx-auto">
                <div className="absolute inset-0 border-4 border-primary/20 rounded-full"></div>
                <div className="absolute inset-1 border-4 border-primary/40 rounded-full animate-spin"></div>
                <div className="absolute inset-2 border-4 border-primary rounded-full animate-pulse"></div>
              </div>
              <div className="space-y-2">
                <p className="text-foreground font-medium">Loading pages...</p>
                <p className="text-muted-foreground text-sm">
                  Setting up your workspace
                </p>
              </div>
            </div>
          </div>
        ) : pagesError ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-destructive mb-4">
                {pagesError.message || "Failed to load pages"}
              </p>
              <button
                onClick={onRetryFetch}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : pages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8">
              <h3 className="text-lg font-semibold mb-2">No Pages Found</h3>
              <p className="text-muted-foreground mb-4">
                Get started by creating your first page
              </p>
              <button
                onClick={onCreatePage}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                Create Page
              </button>
            </div>
          </div>
        ) : currentPageLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-4">
              <div className="relative w-16 h-16 mx-auto">
                <div className="absolute inset-0 border-4 border-primary/20 rounded-full"></div>
                <div className="absolute inset-1 border-4 border-primary/40 rounded-full animate-spin"></div>
                <div className="absolute inset-2 border-4 border-primary rounded-full animate-pulse"></div>
              </div>
              <div className="space-y-2">
                <p className="text-foreground font-medium">
                  Loading page content...
                </p>
                <p className="text-muted-foreground text-sm">
                  Fetching page sections and data
                </p>
              </div>
            </div>
          </div>
        ) : currentPageError ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-destructive mb-4">
                {currentPageError.message || "Failed to load page content"}
              </p>
              <button
                onClick={onRetryFetch}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : (
          <CanvasContent
            currentPage={currentPage}
            storeId={storeId}
            viewportMode={currentDevice}
            selectedSectionId={selectedSectionId}
            onSectionSelect={onSectionSelect}
            dropIndicatorIndex={dropIndicatorIndex}
          />
        )}
      </main>
    </div>
  );
});
